import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import EmptyState from '@/components/base/EmptyState';
import FilmCard from '@/components/base/FilmCard';
import { films, searchFilms, typeLabel } from '@/mocks/films';
import { applySeo } from '@/utils/seo';

type TypeFilter = 'all' | 'movie' | 'tv';

const hotKeywords = ['悬疑', '科幻', '年代', '治愈', '港台', '医疗'];

const typeFilters: { key: TypeFilter; label: string }[] = [
  { key: 'all', label: '全部' },
  { key: 'movie', label: '电影' },
  { key: 'tv', label: '电视剧' },
];

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const keyword = params.get('q') ?? '';
  const [input, setInput] = useState(keyword);
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');

  useEffect(() => {
    setInput(keyword);
    setTypeFilter('all');
  }, [keyword]);

  useEffect(() => {
    applySeo({
      title: keyword
        ? `搜索“${keyword}” - 同学的妈妈影视大全`
        : '搜索影视 - 同学的妈妈影视大全',
      description: keyword
        ? `在同学的妈妈影视大全中搜索“${keyword}”，查看匹配的电影与电视剧剧情介绍。`
        : '按片名、演员、类型或标签搜索电影与电视剧，快速找到想看的影视作品与剧情介绍。',
      keywords: '影视搜索,同学的妈妈,剧情介绍,电影推荐',
      path: keyword ? `/search?q=${encodeURIComponent(keyword)}` : '/search',
    });
  }, [keyword]);

  const matched = useMemo(() => (keyword.trim() ? searchFilms(keyword) : []), [keyword]);

  const results = useMemo(
    () => (typeFilter === 'all' ? matched : matched.filter((film) => film.type === typeFilter)),
    [matched, typeFilter],
  );

  const counts = {
    all: matched.length,
    movie: matched.filter((film) => film.type === 'movie').length,
    tv: matched.filter((film) => film.type === 'tv').length,
  };

  const submit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = input.trim();
    if (value) {
      setParams({ q: value });
    } else {
      setParams({});
    }
  };

  const reset = () => {
    setInput('');
    setParams({});
  };

  return (
    <div className="px-4 md:px-8 lg:px-12">
      <div className="pt-5 md:pt-6">
        <Breadcrumb items={[{ label: '搜索' }]} />
      </div>

      <header className="mt-4">
        <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">搜索影视</h1>
        <p className="mt-2 text-sm text-foreground-600">
          支持片名、原名、演员、类型与标签匹配，共可检索 {films.length} 部影视资料。
        </p>
      </header>

      <form onSubmit={submit} className="mt-5">
        <div className="flex flex-col gap-2.5 sm:flex-row">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-base w-5 h-5 flex items-center justify-center" />
            <input
              id="search-keyword"
              name="q"
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder="输入关键词，例如：悬疑、年代、长夜星河"
              className="h-12 w-full rounded-md border border-background-300 bg-background-50 pl-11 pr-10 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none transition-colors focus:border-primary-400"
            />
            {input && (
              <button
                type="button"
                onClick={() => setInput('')}
                aria-label="清空关键词"
                className="absolute right-3 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full bg-background-200 text-foreground-500 cursor-pointer"
              >
                <i className="ri-close-line text-sm w-4 h-4 flex items-center justify-center" />
              </button>
            )}
          </div>
          <button
            type="submit"
            className="h-12 shrink-0 rounded-md bg-accent-500 px-7 text-sm font-semibold text-foreground-950 transition-colors hover:bg-accent-400 cursor-pointer whitespace-nowrap"
          >
            搜索
          </button>
        </div>
      </form>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span className="text-xs text-foreground-500">热门搜索</span>
        {hotKeywords.map((word) => (
          <button
            key={word}
            type="button"
            onClick={() => {
              setInput(word);
              setParams({ q: word });
            }}
            className="rounded-md bg-background-100 px-2.5 py-1 text-xs text-foreground-700 transition-colors hover:bg-secondary-100 hover:text-secondary-900 cursor-pointer whitespace-nowrap"
          >
            {word}
          </button>
        ))}
      </div>

      <div className="mt-6 border-b border-background-200 pb-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-foreground-700">
            {keyword ? (
              <>
                关键词 <span className="font-semibold text-accent-600">“{keyword}”</span> 的结果
                <span className="ml-2 font-label text-foreground-500">共 {results.length} 条</span>
              </>
            ) : (
              <span className="text-foreground-500">输入关键词开始搜索</span>
            )}
          </p>
          <div className="flex rounded-full bg-background-100 p-1">
            {typeFilters.map((filter) => (
              <button
                key={filter.key}
                type="button"
                onClick={() => setTypeFilter(filter.key)}
                className={`rounded-full px-3.5 py-1.5 text-xs transition-colors cursor-pointer whitespace-nowrap ${
                  typeFilter === filter.key
                    ? 'bg-background-50 font-medium text-foreground-950'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                {filter.label}
                {keyword && (
                  <span className="ml-1 font-label text-[10px] text-foreground-400">{counts[filter.key]}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {!keyword && (
        <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-3">
          {[
            { icon: 'ri-search-eye-line', title: '按片名找', desc: '记得片名的一部分也能搜到' },
            { icon: 'ri-user-star-line', title: '按演员找', desc: '输入主演姓名，查看其作品' },
            { icon: 'ri-price-tag-3-line', title: '按标签找', desc: '试试「治愈」「反转」这类标签' },
          ].map((tip) => (
            <div key={tip.title} className="rounded-lg border border-background-200 bg-background-50 p-4">
              <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                <i className={`${tip.icon} text-base w-4 h-4 flex items-center justify-center`} />
              </span>
              <p className="mt-2.5 text-sm font-medium text-foreground-950">{tip.title}</p>
              <p className="mt-1 text-xs leading-relaxed text-foreground-600">{tip.desc}</p>
            </div>
          ))}
        </div>
      )}

      {keyword && results.length > 0 && (
        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-5 xl:grid-cols-6">
          {results.map((film) => (
            <FilmCard key={film.id} film={film} />
          ))}
        </div>
      )}

      {keyword && results.length === 0 && (
        <div className="mt-6">
          <EmptyState
            title="未找到相关影视内容"
            description={`没有找到与“${keyword}”匹配的影视，建议更换关键词，或浏览下方的热门分类。`}
            resetLabel="清空关键词"
            onReset={reset}
          />
          <div className="mt-6">
            <p className="mb-3 text-xs font-medium text-foreground-500">或许你想看</p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-6">
              {[...films]
                .sort((a, b) => b.popularity - a.popularity)
                .slice(0, 6)
                .map((film) => (
                  <FilmCard key={film.id} film={film} />
                ))}
            </div>
          </div>
          <div className="mt-6 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => navigate('/category/movie')}
              className="rounded-md border border-background-300 px-3 py-1.5 text-xs text-foreground-700 cursor-pointer whitespace-nowrap"
            >
              浏览全部电影
            </button>
            <button
              type="button"
              onClick={() => navigate('/category/tv')}
              className="rounded-md border border-background-300 px-3 py-1.5 text-xs text-foreground-700 cursor-pointer whitespace-nowrap"
            >
              浏览全部{typeLabel.tv}
            </button>
            <Link
              to="/"
              className="rounded-md border border-background-300 px-3 py-1.5 text-xs text-foreground-700 cursor-pointer whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}